/**
 * The desk beside a live call: what the examiner is reading now, what it has
 * read in this interview, and what else its file holds. Built from the
 * records each lookup returned (`state/probeRecords.ts`), matched with the
 * briefing's by id, never guessed from the transcript.
 *
 * @module components/apps/probe/ProbeDesk.logic
 */
import {
  PROBE_WEB_SEARCH_TOOL,
  type ProbeBriefing,
  type ProbeBriefingItems,
  type ProbePolicyFacts,
  type ProbeRecord,
  type ProbeToolName,
  type ProbeTurnLookup,
} from "@amoreg/contracts";

import {
  passageKey,
  recordKey,
  type ProbeRecordRead,
  type ProbeToolArguments,
} from "../../../state/probeRecords";
import {
  byLineAt,
  groupTranscript,
  type ProbeConversationState,
  type ProbeToolCall,
} from "../../../state/probeSession";
import { safeExternalHref } from "../../../lib/safeExternalHref";
import { briefingItems } from "./probeArea";

export type ProbeDeskLink =
  | { readonly kind: "finding"; readonly id: string }
  | { readonly kind: "policy"; readonly id: string }
  | { readonly kind: "url"; readonly url: string };

export interface ProbeDeskCard {
  readonly key: string;
  readonly kind: ProbeRecord["kind"];
  /** "Finding · high", "Regulator change · Isle of Man". */
  readonly label: string;
  readonly severity: string | null;
  readonly title: string;
  readonly detail: string | null;
  readonly link: ProbeDeskLink | null;
}

export interface ProbeDeskRow {
  readonly key: string;
  readonly title: string;
  readonly note: string | null;
  readonly link: ProbeDeskLink | null;
}

export type ProbeDeskEntry =
  | { readonly kind: "card"; readonly card: ProbeDeskCard }
  | {
      readonly kind: "list";
      readonly key: string;
      /** "the open findings", "the policy library". */
      readonly subject: string;
      /** Null for a list whose rows carry no records (people, recommendations). */
      readonly count: number | null;
      readonly cards: ReadonlyArray<ProbeDeskCard>;
    }
  | {
      readonly kind: "nothing";
      readonly key: string;
      readonly title: string;
      readonly query: string | null;
      /** The read succeeded and carries no record (the profile), rather than finding nothing. */
      readonly checked: boolean;
    }
  | {
      readonly kind: "failed";
      readonly key: string;
      readonly title: string;
      readonly reason: string | null;
    };

export interface ProbeDeskReading {
  readonly callId: string;
  readonly title: string;
  readonly detail: string | null;
}

export type ProbeDeskFileSection = "findings" | "updates" | "authority" | "library";

export interface ProbeDeskFileLeft {
  readonly section: ProbeDeskFileSection;
  readonly count: number;
  readonly rows: ReadonlyArray<ProbeDeskRow>;
  readonly unlisted: number;
}

export interface ProbeDeskFile {
  /** Sections with something left, in the briefing's order. */
  readonly sections: ReadonlyArray<ProbeDeskFileLeft>;
  /** False on an older server's briefing, whose lines carry no ids to subtract by. */
  readonly lessRead: boolean;
  readonly empty: boolean;
}

export interface ProbeDesk {
  readonly reading: ReadonlyArray<ProbeDeskReading>;
  readonly read: ReadonlyArray<ProbeDeskEntry>;
  /** Null without a briefing file. */
  readonly file: ProbeDeskFile | null;
}

type NameOf = (kind: "finding" | "policy" | "rehearsal", id: string) => string | null;

type DeskTool =
  | {
      readonly use: "open" | "search";
      readonly title: (args: ProbeToolArguments, nameOf: NameOf) => string;
      readonly detail: (args: ProbeToolArguments, nameOf: NameOf) => string | null;
      readonly nothingFound: (args: ProbeToolArguments, nameOf: NameOf) => string;
      /** The kind of record it reads; null for the profile, which is none. */
      readonly holds: ProbeRecord["kind"] | null;
    }
  | {
      readonly use: "list";
      readonly subject: (args: ProbeToolArguments, nameOf: NameOf) => string;
      readonly counted: boolean;
      /** The kind of record its rows are; null for rows that carry none. */
      readonly holds: ProbeRecord["kind"] | null;
    };

const text = (args: ProbeToolArguments, key: string): string | null => {
  const value = args[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
};

const number = (args: ProbeToolArguments, key: string): number | null => {
  const value = args[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const quoted = (value: string) => `“${value}”`;

const policyName = (args: ProbeToolArguments, nameOf: NameOf): string => {
  const id = text(args, "policyId");
  return (id === null ? null : nameOf("policy", id)) ?? "A policy";
};

const rehearsalName = (args: ProbeToolArguments, nameOf: NameOf): string => {
  const id = text(args, "interviewId");
  return (id === null ? null : nameOf("rehearsal", id)) ?? "A past rehearsal";
};

/** Mid-sentence, an unnamed record reads "a policy", not "A policy". */
const openedNothing =
  (name: (args: ProbeToolArguments, nameOf: NameOf) => string) =>
  (args: ProbeToolArguments, nameOf: NameOf) => {
    const named = name(args, nameOf);
    const said = named === "A policy" || named === "A past rehearsal" ? named.toLowerCase() : named;
    return `Opened ${said}: nothing came back`;
  };

/**
 * Every Probe tool, once: whether it opens one record, searches, or lists,
 * and how to say what it is reading from its arguments. Opened and searched
 * records become cards; a list is one row, however many rows it returned.
 */
export const PROBE_DESK_TOOLS: Readonly<Record<ProbeToolName, DeskTool>> = {
  governance_get_context: {
    use: "open",
    title: () => "The organization's profile",
    detail: () => null,
    holds: null,
    nothingFound: () => "Checked the organization's profile",
  },
  governance_get_policy: {
    use: "open",
    title: policyName,
    detail: () => "Its record and findings",
    holds: "policy",
    nothingFound: openedNothing(policyName),
  },
  governance_get_policy_text: {
    use: "open",
    title: policyName,
    detail: (args) => ((number(args, "offset") ?? 0) > 0 ? "Further into its text" : "Its text"),
    holds: "policy",
    nothingFound: openedNothing(policyName),
  },
  governance_get_probe_interview: {
    use: "open",
    title: rehearsalName,
    detail: (args, nameOf) => {
      const id = text(args, "interviewId");
      return id !== null && nameOf("rehearsal", id) !== null ? "A past rehearsal" : null;
    },
    holds: "rehearsal",
    nothingFound: openedNothing(rehearsalName),
  },
  governance_search_knowledge: {
    use: "search",
    title: (args) => quoted(text(args, "question") ?? "A question"),
    detail: (args) => {
      const jurisdiction = text(args, "jurisdiction");
      return jurisdiction === null
        ? "In the knowledge base"
        : `In the knowledge base · ${jurisdiction}`;
    },
    holds: "passage",
    nothingFound: () => "Searched the knowledge base: nothing found",
  },
  [PROBE_WEB_SEARCH_TOOL]: {
    use: "search",
    title: (args) => quoted(text(args, "query") ?? "A search"),
    detail: () => "On the web",
    holds: "web",
    nothingFound: () => "Searched the web: nothing found",
  },
  governance_list_findings: {
    use: "list",
    subject: (args, nameOf) => {
      const status = text(args, "status");
      const policyId = text(args, "policyId");
      const policy = policyId === null ? null : (nameOf("policy", policyId) ?? "one policy");
      return `the ${status === null ? "" : `${status} `}findings${policy === null ? "" : ` on ${policy}`}`;
    },
    counted: true,
    holds: "finding",
  },
  governance_list_policies: {
    use: "list",
    subject: (args) => {
      const query = text(args, "query");
      return query === null ? "the policy library" : `the policy library for ${quoted(query)}`;
    },
    counted: true,
    holds: "policy",
  },
  governance_list_regulatory_updates: {
    use: "list",
    subject: (args) => {
      const days = number(args, "withinDays");
      return days === null
        ? "the regulator changes"
        : `the regulator changes of the last ${days} days`;
    },
    counted: true,
    holds: "update",
  },
  governance_list_probe_interviews: {
    use: "list",
    subject: (args) =>
      text(args, "author") === "me" ? "your past rehearsals" : "the past rehearsals",
    counted: true,
    holds: "rehearsal",
  },
  governance_list_people: {
    use: "list",
    subject: () => "the organization's people",
    counted: false,
    holds: null,
  },
  governance_list_recommendations: {
    use: "list",
    subject: (args) => {
      const status = text(args, "status");
      return `the ${status === null ? "" : `${status} `}recommendations`;
    },
    counted: false,
    holds: null,
  },
};

const deskTool = (name: string): DeskTool | null =>
  Object.hasOwn(PROBE_DESK_TOOLS, name) ? PROBE_DESK_TOOLS[name as ProbeToolName] : null;

/** The kind of record a lookup reads, and the one record it opens when its arguments name it. */
export const deskToolTarget = (
  tool: Pick<ProbeToolCall, "name" | "arguments">,
): { readonly kind: ProbeRecord["kind"] | null; readonly key: string | null } => {
  const entry = deskTool(tool.name);
  if (entry === null) return { kind: null, key: null };
  const named =
    entry.use !== "open" || entry.holds === null
      ? null
      : (text(tool.arguments, entry.holds === "rehearsal" ? "interviewId" : "policyId") ?? null);
  return { kind: entry.holds, key: named === null ? null : `${entry.holds}:${named}` };
};

const canonicalJson = (value: unknown): string =>
  JSON.stringify(value, (_key, nested: unknown) =>
    typeof nested === "object" && nested !== null && !Array.isArray(nested)
      ? Object.fromEntries(
          Object.entries(nested).toSorted(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : nested,
  );

const lookupKey = (tool: ProbeToolCall): string => `${tool.name}:${canonicalJson(tool.arguments)}`;

const sentence = (phrase: string) => phrase.charAt(0).toUpperCase() + phrase.slice(1);

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const EMPTY_ITEMS: ProbeBriefingItems = { findings: [], updates: [], authority: [], library: [] };

const namesOf = (records: ReadonlyArray<ProbeRecordRead>, items: ProbeBriefingItems): NameOf => {
  const names = new Map<string, string>();
  for (const entry of items.library)
    if (entry.id !== null) names.set(`policy:${entry.id}`, entry.name);
  for (const finding of items.findings)
    if (finding.id !== null) names.set(`finding:${finding.id}`, finding.title);
  for (const { record } of records) {
    const title =
      record.kind === "finding" || record.kind === "policy" || record.kind === "rehearsal"
        ? record.title
        : null;
    if (title !== null) names.set(recordKey(record), title);
  }
  return (kind, id) => names.get(`${kind}:${id}`) ?? null;
};

type FactsOf = (policyId: string) => ProbePolicyFacts | null;

const factsOfLine = (line: string): ProbePolicyFacts => {
  const [, lifecycle = null, ...rest] = line.split(", ");
  const tagged = (prefix: string) =>
    rest.find((part) => part.startsWith(prefix))?.slice(prefix.length) ?? null;
  return { lifecycle, owner: tagged("owner "), reviewDue: tagged("review due ") };
};

const factsOfPolicies = (
  records: ReadonlyArray<ProbeRecordRead>,
  items: ProbeBriefingItems,
): FactsOf => {
  const facts = new Map<string, ProbePolicyFacts>();
  for (const { record } of records)
    if (record.kind === "policy" && record.facts !== null && !facts.has(record.id))
      facts.set(record.id, record.facts);
  return (policyId) => {
    const line = items.library.find((entry) => entry.id === policyId)?.facts;
    return facts.get(policyId) ?? (line ? factsOfLine(line) : null);
  };
};

/** `2027-03-31` → `31 Mar 2027`; anything that is not a day is left as it came. */
export const deskDate = (day: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day;
  const time = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(time)) return day;
  const date = new Date(time);
  // en-GB shortens September to "Sept"; every month here is three letters.
  const month = date.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }).slice(0, 3);
  return `${date.getUTCDate()} ${month} ${date.getUTCFullYear()}`;
};

type OwnerName = (owner: string) => string;

/**
 * A policy owner as a person reads them: the briefing's name for the person
 * on the call, else the name an address like `priya.shah@…` spells, else the
 * address (a role mailbox such as `mlro@…` names no one).
 */
export const ownerNameOf =
  (briefing: ProbeBriefing | null): OwnerName =>
  (owner) => {
    const person = briefing?.person;
    if (person?.name && person.email.toLowerCase() === owner.toLowerCase()) return person.name;
    const [local, domain] = owner.split("@");
    if (local === undefined || domain === undefined) return owner;
    const parts = local.split(/[._-]+/).filter((part) => part.length > 0);
    return parts.length >= 2 && parts.every((part) => /^\p{L}+$/u.test(part))
      ? parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ")
      : owner;
  };

const policyDetail = (facts: ProbePolicyFacts | null, ownerName: OwnerName): string | null => {
  if (facts === null) return null;
  const parts = [
    facts.lifecycle === null ? null : sentence(facts.lifecycle.replace(/_/g, " ")),
    facts.owner === null ? null : `owner ${ownerName(facts.owner)}`,
    facts.reviewDue === null ? null : `review due ${deskDate(facts.reviewDue)}`,
  ].filter((part) => part !== null);
  return parts.length === 0 ? null : parts.join(" · ");
};

const cardOf = (
  record: ProbeRecord,
  items: ProbeBriefingItems,
  nameOf: NameOf,
  factsOf: FactsOf,
  ownerName: OwnerName,
): ProbeDeskCard => {
  const base = { key: recordKey(record), kind: record.kind };
  switch (record.kind) {
    case "finding": {
      const filed = items.findings.find((finding) => finding.id === record.id);
      const severity = record.severity ?? filed?.severity ?? null;
      return {
        ...base,
        label: severity === null ? "Finding" : `Finding · ${severity}`,
        severity,
        title: record.title,
        detail: filed?.summary ?? null,
        link: { kind: "finding", id: record.id },
      };
    }
    case "policy":
      return {
        ...base,
        label: "Policy",
        severity: null,
        title: record.title ?? nameOf("policy", record.id) ?? "Untitled document",
        detail: policyDetail(factsOf(record.id), ownerName),
        link: { kind: "policy", id: record.id },
      };
    case "update":
      return {
        ...base,
        label: `Regulator change · ${record.jurisdiction}`,
        severity: null,
        title: record.title,
        detail: record.date === null ? null : deskDate(record.date),
        link: webLink(record.url),
      };
    case "passage":
      return {
        ...base,
        label: "Authority passage",
        severity: null,
        title: record.title,
        detail: record.text,
        link: null,
      };
    case "rehearsal":
      return {
        ...base,
        label: "Past rehearsal",
        severity: null,
        title: record.title,
        detail: deskDate(record.date),
        link: null,
      };
    case "web":
      return {
        ...base,
        label: `Web page · ${hostOf(record.url)}`,
        severity: null,
        title: record.title,
        detail: null,
        link: webLink(record.url),
      };
  }
};

/** A link to a web page, or null when the address is not an http(s) one: nothing else is ever drawn as a link. */
const webLink = (url: string | null): ProbeDeskLink | null => {
  const href = safeExternalHref(url);
  return href === null ? null : { kind: "url", url: href };
};

export interface ProbeDeskFileLine extends Omit<ProbeDeskRow, "key"> {
  /** Every key under which a finished lookup could have returned this item. */
  readonly readKeys: ReadonlyArray<string>;
}

/** The briefing's items by section, each with the keys a lookup could have read it under. */
export const fileLines = (
  items: ProbeBriefingItems,
): ReadonlyArray<readonly [ProbeDeskFileSection, ReadonlyArray<ProbeDeskFileLine>]> => [
  [
    "findings",
    items.findings.map(({ id, title, severity }) => ({
      readKeys: id === null ? [] : [`finding:${id}`],
      title,
      note: severity,
      link: id === null ? null : { kind: "finding", id },
    })),
  ],
  [
    "updates",
    items.updates.map(({ id, title, date, url }) => ({
      // A regulator change the examiner opened as a web page has been read too.
      readKeys: [...(id === null ? [] : [`update:${id}`]), ...(url === null ? [] : [`web:${url}`])],
      title,
      note: date === null ? null : deskDate(date),
      link: webLink(url),
    })),
  ],
  [
    "authority",
    items.authority.map((passage) => ({
      readKeys: [`passage:${passageKey(passage)}`],
      title: passage.source ?? passage.text,
      note: passage.jurisdiction.length > 0 ? passage.jurisdiction : null,
      link: null,
    })),
  ],
  [
    "library",
    items.library.map(({ id, name }) => ({
      readKeys: id === null ? [] : [`policy:${id}`],
      title: name,
      note: null,
      link: id === null ? null : { kind: "policy", id },
    })),
  ],
];

const fileOf = (
  briefing: ProbeBriefing | null,
  touched: ReadonlySet<string>,
): ProbeDeskFile | null => {
  const file = briefing?.file;
  if (file === undefined) return null;
  const lessRead = file.items !== undefined;
  const items = briefingItems(file);
  const unread = (readKeys: ReadonlyArray<string>) =>
    !lessRead || !readKeys.some((key) => touched.has(key));
  const listed = new Set(items.library.flatMap((entry) => entry.id ?? []));
  const readUnlisted = lessRead
    ? [...touched].filter(
        (key) => key.startsWith("policy:") && !listed.has(key.slice("policy:".length)),
      ).length
    : 0;
  const unlisted = Math.max(0, file.libraryTotal - items.library.length - readUnlisted);
  return {
    sections: fileLines(items).flatMap(([section, lines]) => {
      const rows = lines.flatMap(({ readKeys, ...row }, index) =>
        unread(readKeys) ? [{ key: `${section}:${index}`, ...row }] : [],
      );
      const beyond = section === "library" ? unlisted : 0;
      const count = rows.length + beyond;
      return count > 0 ? [{ section, count, rows, unlisted: beyond }] : [];
    }),
    lessRead,
    empty:
      file.findings.length === 0 &&
      file.updates.length === 0 &&
      file.authority.length === 0 &&
      file.libraryTotal === 0,
  };
};

const readingOf = (tool: ProbeToolCall, nameOf: NameOf): ProbeDeskReading => {
  const entry = deskTool(tool.name);
  if (entry === null) return { callId: tool.callId, title: tool.name, detail: null };
  return entry.use === "list"
    ? { callId: tool.callId, title: sentence(entry.subject(tool.arguments, nameOf)), detail: null }
    : {
        callId: tool.callId,
        title: entry.title(tool.arguments, nameOf),
        detail: entry.detail(tool.arguments, nameOf),
      };
};

/**
 * What is being read this moment, each record once: opening a policy's record
 * and its text at the same time reads one policy.
 */
const readingNow = (
  tools: ReadonlyArray<ProbeToolCall>,
  nameOf: NameOf,
): ReadonlyArray<ProbeDeskReading> => {
  const targets = new Set<string>();
  return tools.flatMap((tool) => {
    if (tool.status !== "running") return [];
    const { key } = deskToolTarget(tool);
    if (key !== null) {
      if (targets.has(key)) return [];
      targets.add(key);
    }
    return [readingOf(tool, nameOf)];
  });
};

export const deskReadCount = (desk: ProbeDesk): number =>
  desk.read.filter((entry) => entry.kind !== "failed").length;

export type ProbeDeskInput = Pick<ProbeConversationState, "tools" | "records">;

const savedCallId = (turnIndex: number, index: number) => `saved-${turnIndex}-${index}`;

export const savedDeskInput = (
  turns: ReadonlyArray<{ readonly lookups: ReadonlyArray<ProbeTurnLookup> }>,
): ProbeDeskInput => {
  const tools: Array<ProbeToolCall> = [];
  const records: Array<ProbeRecordRead> = [];
  turns.forEach((turn, turnIndex) => {
    turn.lookups.forEach((lookup, index) => {
      const callId = savedCallId(turnIndex, index);
      const { name, arguments: args, status, reason, atMs } = lookup;
      tools.push({ callId, name, arguments: args, status, reason, atMs });
      for (const record of lookup.records) records.push({ callId, atMs, record });
    });
  });
  return { tools, records };
};

/** A line of the interview, with the lookups that finished before it and after the line before. */
export interface ProbeDeskTurn {
  /** The transcript line's id on a live call; the turn's index on a saved interview. */
  readonly key: string;
  readonly role: "user" | "assistant";
  readonly text: string;
  readonly callIds: ReadonlyArray<string>;
}

/** The call as a desk reads it: its lookups and records, and the lines they were read for. */
export interface ProbeDeskCall extends ProbeDeskInput {
  readonly turns: ReadonlyArray<ProbeDeskTurn>;
}

/** A live call's lines, each with the lookups placed under it as a save would place them (`savedTurns`). */
export const liveDeskCall = (
  state: Pick<ProbeConversationState, "tools" | "records" | "transcript">,
): ProbeDeskCall => {
  const items = groupTranscript(state.transcript);
  const placed = byLineAt(
    items,
    state.tools.flatMap((tool) =>
      tool.status === "running" || tool.atMs === null ? [] : [{ ...tool, atMs: tool.atMs }],
    ),
  );
  return {
    tools: state.tools,
    records: state.records,
    turns: items.map((item) => ({
      key: item.entry.id,
      role: item.kind === "answer" ? "user" : "assistant",
      text: item.entry.text.trim(),
      callIds: (placed.get(item.entry.id) ?? []).map((tool) => tool.callId),
    })),
  };
};

export const savedDeskCall = (
  turns: ReadonlyArray<{
    readonly role: "user" | "assistant";
    readonly text: string;
    readonly lookups: ReadonlyArray<ProbeTurnLookup>;
  }>,
): ProbeDeskCall => ({
  ...savedDeskInput(turns),
  turns: turns.map((turn, turnIndex) => ({
    key: String(turnIndex),
    role: turn.role,
    text: turn.text.trim(),
    callIds: turn.lookups.map((_lookup, index) => savedCallId(turnIndex, index)),
  })),
});

/** One finished lookup, as every desk reads it. */
export type ProbeDeskLookup =
  | {
      readonly kind: "failed";
      readonly callId: string;
      readonly title: string;
      readonly reason: string | null;
    }
  | {
      readonly kind: "list";
      readonly callId: string;
      /** The same list read twice shares a key. */
      readonly key: string;
      readonly subject: string;
      readonly count: number | null;
      readonly holds: ProbeRecord["kind"] | null;
      readonly records: ReadonlyArray<ProbeRecord>;
    }
  | {
      readonly kind: "nothing";
      readonly callId: string;
      readonly key: string;
      readonly title: string;
      readonly query: string | null;
      readonly checked: boolean;
    }
  | {
      /** An open or a search that returned records. */
      readonly kind: "opened";
      readonly callId: string;
      readonly use: "open" | "search";
      /** The policy opened, or the search's words. */
      readonly asked: string;
      readonly records: ReadonlyArray<ProbeRecord>;
    };

export interface ProbeDeskLookups {
  readonly reading: ReadonlyArray<ProbeDeskReading>;
  /** In the order they finished. */
  readonly finished: ReadonlyArray<ProbeDeskLookup>;
  /** A record named and described from the lookups and the briefing. */
  readonly cardOf: (record: ProbeRecord) => ProbeDeskCard;
  readonly policyFacts: (policyId: string) => ProbePolicyFacts | null;
  readonly ownerName: OwnerName;
  /** The briefing's items; empty without them. */
  readonly items: ProbeBriefingItems;
  readonly file: ProbeDeskFile | null;
  /** Every record key a finished lookup returned. */
  readonly touched: ReadonlySet<string>;
}

export const deskLookupsOf = (
  state: ProbeDeskInput,
  briefing: ProbeBriefing | null,
): ProbeDeskLookups => {
  const items = briefing?.file?.items ?? EMPTY_ITEMS;
  const nameOf = namesOf(state.records, items);
  const factsOf = factsOfPolicies(state.records, items);
  const byCall = new Map<string, Array<ProbeRecord>>();
  for (const { callId, record } of state.records) {
    const reads = byCall.get(callId);
    if (reads === undefined) byCall.set(callId, [record]);
    else reads.push(record);
  }
  const finished = state.tools
    .filter((tool) => tool.status !== "running")
    .sort((one, other) => (one.atMs ?? 0) - (other.atMs ?? 0))
    .flatMap((tool): ReadonlyArray<ProbeDeskLookup> => {
      const { callId } = tool;
      if (tool.status === "failed")
        return [{ kind: "failed", callId, title: readingOf(tool, nameOf).title, reason: tool.reason }];
      const entry = deskTool(tool.name);
      if (entry === null) return [];
      const records = byCall.get(callId) ?? [];
      if (entry.use === "list")
        return [
          {
            kind: "list",
            callId,
            key: `list:${lookupKey(tool)}`,
            subject: entry.subject(tool.arguments, nameOf),
            count: entry.counted ? records.length : null,
            holds: entry.holds,
            records,
          },
        ];
      if (records.length === 0)
        return [
          {
            kind: "nothing",
            callId,
            key: `nothing:${lookupKey(tool)}`,
            title: entry.nothingFound(tool.arguments, nameOf),
            query: entry.use === "search" ? entry.title(tool.arguments, nameOf) : null,
            checked: tool.name === "governance_get_context",
          },
        ];
      return [
        { kind: "opened", callId, use: entry.use, asked: entry.title(tool.arguments, nameOf), records },
      ];
    });
  const touched = new Set(state.records.map(({ record }) => recordKey(record)));
  const ownerName = ownerNameOf(briefing);
  return {
    reading: readingNow(state.tools, nameOf),
    finished,
    cardOf: (record) => cardOf(record, items, nameOf, factsOf, ownerName),
    policyFacts: factsOf,
    ownerName,
    items,
    file: fileOf(briefing, touched),
    touched,
  };
};

export const deskOf = (state: ProbeDeskInput, briefing: ProbeBriefing | null): ProbeDesk => {
  const lookups = deskLookupsOf(state, briefing);
  const read: Array<ProbeDeskEntry> = [];
  const cards = new Set<string>();
  const rows = new Map<string, number>();
  const putRow = (row: Extract<ProbeDeskEntry, { kind: "list" | "nothing" }>) => {
    const at = rows.get(row.key);
    if (at === undefined) {
      rows.set(row.key, read.length);
      read.push(row);
    } else read[at] = row;
  };
  for (const lookup of lookups.finished) {
    switch (lookup.kind) {
      case "failed":
        read.push({ kind: "failed", key: lookup.callId, title: lookup.title, reason: lookup.reason });
        break;
      case "list":
        putRow({
          kind: "list",
          key: lookup.key,
          subject: lookup.subject,
          count: lookup.count,
          cards: lookup.records.map(lookups.cardOf),
        });
        break;
      case "nothing": {
        const { callId: _callId, ...row } = lookup;
        putRow(row);
        break;
      }
      case "opened":
        for (const record of lookup.records) {
          const key = recordKey(record);
          if (cards.has(key)) continue;
          cards.add(key);
          read.push({ kind: "card", card: lookups.cardOf(record) });
        }
    }
  }
  return { reading: lookups.reading, read, file: lookups.file };
};
